import type { Controller, OrgId } from "@ctrl/controller-rpc";

import type { UseProfile } from "@/composables/useProfile";

/** ID prefix used by the personal assistant (see schemas/src/ids.ml). */
const ASSISTANT_PREFIX = "assistant:";

export function isAssistant(agentId: string): boolean {
  return agentId.startsWith(ASSISTANT_PREFIX);
}

export function isAgent(agentId: string): boolean {
  return !!agentId && !agentId.startsWith(ASSISTANT_PREFIX);
}

/**
 * Resolve which organization owns a given agent or assistant ID.
 * Checks regular agents via `ai.agent.all` first, then falls back
 * to prefix-based detection for the personal assistant (whose ID is
 * not included in `ai.agent.all`).
 */
export async function resolveAgentOrganization(
  controller: Controller,
  profile: UseProfile,
  agentId: string
): Promise<OrgId | null> {
  if (!agentId) return null;

  // Fast path: check the regular agents list.
  for (const organization of profile.organizations) {
    const agentIdsRef = await controller.ai.agent.all(organization.id);
    const agentIds: string[] = agentIdsRef.release() ?? [];
    if (agentIds.includes(agentId)) return organization.id;
  }

  // The personal assistant is not listed by ai.agent.all but uses
  // the same ai.agent.get / conversation RPCs (AnyAgentId).
  // Resolve to the user's personal org so entitlement checks match
  // the subscription that actually grants assistant access.
  if (isAssistant(agentId)) return profile.user.personal.organizationId;

  return null;
}
